import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { H2, color, shadow, space, device, radius } from '@ticketswap/solar'
import { Alert, Ticket, SellTicket } from '@ticketswap/comets'
import AppStoreBadges from '~/components/AppStoreBadges'
import AppsAdoptionNotification from '~/components/AppsAdoptionNotification'
import mobileScreen from './mobile-screen.jpg'
import mobileScreenSmaller from './mobile-screen-smaller.jpg'
import mobileScreenBigger from './mobile-screen-bigger.jpg'
import mobileScreenBiggerDark from './mobile-screen-bigger-dark.jpeg'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${space[64]};
  text-align: center;

  @media ${device.tablet} {
    margin-bottom: 0;
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    text-align: left;
  }
`

const StyledH2 = styled(H2)`
  margin-bottom: ${space[16]};

  @media ${device.tablet} {
    margin-bottom: ${space[32]};
  }
`

const Illustration = styled.div`
  display: block;
  position: relative;

  @media ${device.tablet} {
    width: 55%;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`

const StyledAppsAdoptionNotification = styled(AppsAdoptionNotification)`
  left: 0;
  margin: auto;
  position: absolute;
  right: 0;
  top: 46%;
  width: 68%;

  @media ${device.tablet} {
    width: 64%;
  }
`

const InfoContainer = styled.div`
  @media ${device.tablet} {
    width: 40%;
    flex-basis: 40%;
  }
`

const Item = styled.li`
  & + ${() => Item} {
    margin-left: ${space[16]};
  }
`

const StyledIconContainer = styled.span`
  display: none;

  @media ${device.tablet} {
    display: flex;
    width: 2.5rem;
    height: 2.5rem;
    margin-right: ${space[16]};
    background-color: ${color.spaceLighter};
    justify-content: center;
    align-items: center;
    color: ${color.nova};
    background-color: ${props => props.bgColor};
    border-radius: ${radius.md};

    svg {
      filter: drop-shadow(${shadow.text});
    }

    .icon {
      transform: scale(1);
    }
  }
`

const Usps = styled.ul`
  margin-bottom: ${space[32]};
  display: inline-block;

  @media ${device.tablet} {
    margin-bottom: ${space[48]};
  }
`

const Usp = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.spaceMedium};

  @media ${device.tablet} {
    justify-content: flex-start;
  }

  & + ${() => Usp} {
    margin-top: ${space[8]};

    @media ${device.tablet} {
      margin-top: ${space[16]};
    }
  }
`

const Image = styled.img`
  [data-theme='dark'] & {
    display: none;
  }
`

const ImageDark = styled.img`
  display: none;

  [data-theme='dark'] & {
    display: block;
  }
`

const AppsPayoff = ({ t, ...props }) => {
  return (
    <Container {...props}>
      <Illustration>
        <Image
          src={mobileScreen}
          srcSet={`${mobileScreenSmaller} 480w, ${mobileScreen} 800w, ${mobileScreenBigger} 1200w`}
          sizes={`(min-width: ${device.tablet}) calc(55vw - 64px), calc(100vw - 32px)`}
          alt={t('common:apps-pay-off.title')}
          loading="lazy"
        />

        <ImageDark
          src={mobileScreenBiggerDark}
          alt={t('common:apps-pay-off.title')}
          loading="lazy"
        />

        <StyledAppsAdoptionNotification t={t} />
      </Illustration>

      <InfoContainer>
        <StyledH2>{t('common:apps-pay-off.title')}</StyledH2>

        <Usps>
          <Usp>
            <StyledIconContainer>
              <Ticket size={24} />
            </StyledIconContainer>
            {t('common:apps-pay-off.usps.alpha')}
          </Usp>
          <Usp>
            <StyledIconContainer>
              <Alert size={24} />
            </StyledIconContainer>
            {t('common:apps-pay-off.usps.beta')}
          </Usp>
          <Usp>
            <StyledIconContainer>
              <SellTicket size={24} />
            </StyledIconContainer>
            {t('common:apps-pay-off.usps.gamma')}
          </Usp>
        </Usps>

        <AppStoreBadges trackingContext="APP_USP" />
      </InfoContainer>
    </Container>
  )
}

AppsPayoff.propTypes = {
  t: PropTypes.func.isRequired,
}

export default AppsPayoff
